"use client"
import "./LoginForm.css"
import { useState } from "react"
import axios from "../utils/axios"
import taaounityLogo from "../logos/taaounity.png"
import { User, Eye, EyeOff, Lock } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { GoogleLogin } from '@react-oauth/google'
import Notification from "./Notification"
import { forceRefreshFeatureFlags } from "../utils/featureFlags"
import { getPostAuthPath } from "../utils/postAuth"

function getLoginNext() {
  const params = new URLSearchParams(window.location.search)
  return params.get("next") || ""
}

export default function LoginForm({ language, translations }) {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [notification, setNotification] = useState({ show: false, message: "", type: "error" })

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value })

  const errorMessageMap = {
    "Given token not valid for any token type": {
      en: "Invalid username or password.",
      fr: "Nom d'utilisateur ou mot de passe invalide.",
      ar: "اسم المستخدم أو كلمة المرور غير صحيحة."
    },
    // Add more mappings as needed
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { data } = await axios.post("/api/auth/login/", formData)
      
              // Login successful

      // Handle different response formats
      const accessToken = data.access || data.access_token || data.token
      const refreshToken = data.refresh || data.refresh_token

      if (!accessToken) {
        throw new Error("No access token received")
      }

      // Save tokens
      localStorage.setItem("access_token", accessToken)
      localStorage.setItem("refresh_token", refreshToken)
      localStorage.setItem("username", formData.username)

      // Set token for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`

      // Check if user is a team member and store member data
      try {
        const memberResponse = await axios.get('/api/membres/');
        const currentUser = data.user?.username || formData.username;
        
        // Find if current user is a member
        const memberData = memberResponse.data.find(member => 
          member.auth_user?.username === currentUser || 
          member.nom === currentUser
        );
        
        if (memberData) {
          // User is a team member
          localStorage.setItem("is_team_member", "true");
          localStorage.setItem("member_data", JSON.stringify(memberData));
        } else {
          // User is the owner
          localStorage.setItem("is_team_member", "false");
          localStorage.removeItem("member_data");
        }
      } catch (memberError) {
        // Error checking member status
        // Default to owner if we can't check
        localStorage.setItem("is_team_member", "false");
        localStorage.removeItem("member_data");
      }

      // Handle plan assignment if user had selected one
      const selectedPlan = localStorage.getItem("selected_plan")

      if (!data.needs_onboarding && selectedPlan && selectedPlan !== "trial") {
        try {
          await axios.post("/plans/assign/", {
          plan_id: selectedPlan
        }, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        })
        } catch (planError) {
          // Plan assignment failed, continuing with login
        }

        localStorage.removeItem("selected_plan")
      }

      setNotification({
        show: true,
        message: translations?.login_success?.[language] || "Login successful!",
        type: "success"
      })
      
      // Trigger feature flags refresh after login
      window.dispatchEvent(new CustomEvent('userLoggedIn'));
      try {
        await forceRefreshFeatureFlags();
      } catch (refreshError) {
        console.warn("Feature flag prefetch failed after login", refreshError);
      }
      
      setTimeout(() => navigate(getPostAuthPath({
        requiresPhone: data.requires_phone,
        needsOnboarding: data.needs_onboarding,
        useWorkspace: data.use_workspace,
        next: getLoginNext(),
      }), { state: { success: "login_success" } }), 1000)

    } catch (err) {
      const backendMsg = err.response?.data?.detail || err.response?.data?.error
      let userMsg = translations?.login_error?.[language] || "Account does not exist or password is incorrect."
      if (backendMsg && errorMessageMap[backendMsg]) {
        userMsg = errorMessageMap[backendMsg][language] || errorMessageMap[backendMsg].en
      }
      setNotification({
        show: true,
        message: userMsg,
        type: "error"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      // Google credential received

      const response = await axios.post("/api/auth/google-one-tap/", {
        id_token: credentialResponse.credential,
      })

      // Save tokens
      localStorage.setItem('access_token', response.data.access)
      localStorage.setItem('refresh_token', response.data.refresh)
      localStorage.setItem('username', response.data.user?.username || response.data.username || 'Utilisateur')

      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`

      // Check if user is a team member and store member data
      try {
        const memberResponse = await axios.get('/api/membres/');
        const currentUser = response.data.user?.username || response.data.username || 'Utilisateur';
        
        // Find if current user is a member
        const memberData = memberResponse.data.find(member => 
          member.auth_user?.username === currentUser || 
          member.nom === currentUser
        );
        
        if (memberData) {
          // User is a team member
          localStorage.setItem("is_team_member", "true");
          localStorage.setItem("member_data", JSON.stringify(memberData));
        } else {
          // User is the owner
          localStorage.setItem("is_team_member", "false");
          localStorage.removeItem("member_data");
        }
      } catch (memberError) {
        // Error checking member status
        // Default to owner if we can't check
        localStorage.setItem("is_team_member", "false");
        localStorage.removeItem("member_data");
      }

      // Handle plan assignment
      const selectedPlan = localStorage.getItem("selected_plan")

      if (!response.data.needs_onboarding && selectedPlan && selectedPlan !== "trial") {
        try {
          await axios.post("/plans/assign/", {
          plan_id: selectedPlan
        }, {
          headers: {
            Authorization: `Bearer ${response.data.access}`
          }
        })
        } catch (planError) {
          // Plan assignment failed, continuing with login
        }

        localStorage.removeItem("selected_plan")
      }

      window.dispatchEvent(new CustomEvent('userLoggedIn'));
      try {
        await forceRefreshFeatureFlags();
      } catch (refreshError) {
        console.warn("Feature flag prefetch failed after Google login", refreshError);
      }

      if (response.data.requires_phone) {
        localStorage.setItem("signup_source", "google_one_tap");
        navigate("/complete-profile");
        return;
      }

      navigate(getPostAuthPath({
        needsOnboarding: response.data.needs_onboarding,
        useWorkspace: response.data.use_workspace,
        next: getLoginNext(),
      }), { state: { success: "login_success" } })

    } catch (error) {
      console.error('Google login error:', error.response?.data || error)
      const backendDetail = error.response?.data?.detail
      const backendError = error.response?.data?.error
      let errorMsg =
        translations?.google_login_error?.[language] ||
        backendDetail ||
        backendError ||
        "Échec de la connexion Google"
      if (
        typeof window !== "undefined" &&
        window.location.hostname === "localhost" &&
        !backendDetail &&
        !backendError
      ) {
        errorMsg +=
          language === "fr"
            ? " — Vérifiez que http://localhost:3000 est autorisé dans Google Cloud Console."
            : language === "ar"
              ? " — تأكد من إضافة http://localhost:3000 في Google Cloud Console."
              : " — Ensure http://localhost:3000 is allowed in Google Cloud Console."
      }
      setNotification({
        show: true,
        message: errorMsg,
        type: "error"
      })
    }
  }

  return (
    <div className="login-page">
      <Notification
        show={notification.show}
        message={notification.message}
        type={notification.type}
        language={language}
        translations={translations}
        onClose={() => setNotification({ ...notification, show: false })}
      />
      <div className="loginform">
        <form onSubmit={handleSubmit} className="login-form">
          <div
            className={`login-brand${language === "ar" ? " login-brand--rtl" : ""}`}
          >
            <img
              src={taaounityLogo}
              alt=""
              className="login-logo"
              width={180}
              height={56}
            />
            <div className="login-brand-text">
              <h2 className="login-brand-title">Taaouniaty</h2>
              <span className="login-brand-tagline">Pro</span>
            </div>
          </div>

          {/* Username */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <User />
              </div>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'اسم المستخدم' : language === 'fr' ? "Nom d'utilisateur" : 'Username'}
                required
                className="form-input"
                autoComplete="username"
              />
            </div>
          </div>

          {/* Password */}
          <div className="form-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <Lock />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={language === 'ar' ? 'كلمة المرور' : language === 'fr' ? 'Mot de passe' : 'Password'}
                required
                className="form-input"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="password-toggle"
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>

          {/* Options Row */}
          <div className="options-row">
            <label className="remember-me">
              <input type="checkbox" />
              {language === 'ar' ? 'تذكرني' : language === 'fr' ? 'Se souvenir de moi' : 'Remember me'}
            </label>
            <a href="#" className="forgot-password">{language === 'ar' ? 'نسيت كلمة المرور؟' : language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}</a>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? (
              <span className="loading-text">
                <span className="spinner"></span>
                {language === 'ar' ? 'جارٍ تسجيل الدخول…' : language === 'fr' ? 'Connexion…' : 'Signing in…'}
              </span>
            ) : (
              (language === 'ar' ? 'تسجيل الدخول' : language === 'fr' ? 'Se connecter' : 'Sign in')
            )}
          </button>

          <p className="signup-text">
            {language === 'ar' ? 'ليس لديك حساب؟' : language === 'fr' ? "Vous n'avez pas de compte ?" : "Don't have an account?"}{" "}
            <Link to="/register" className="signup-link">{language === 'ar' ? 'إنشاء حساب' : language === 'fr' ? 'Créer un compte' : 'Create account'}</Link>
          </p>

          {/* Divider */}
          <div className="divider">{language === 'ar' ? 'أو المتابعة باستخدام' : language === 'fr' ? 'Ou continuer avec' : 'Or continue with'}</div>

          {/* Google Login */}
          <div className="google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleLoginSuccess}
              onError={() => alert("Échec de la connexion Google")}
            />
          </div>
        </form>
      </div>
    </div>
  )
}
